import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';
import { reduxForm, initialize, formValueSelector, getFormSyncErrors, getFormValues } from 'redux-form';
import Validation from 'infrastructure/js/components/controls/controlsValidations.js';
import Dialog from 'infrastructure/js/components/Dialog/dialog';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import Combobox from 'infrastructure/js/components/controls/Combobox/combobox.js';
import TimeField from 'infrastructure/js/components/controls/TimeField/timeField';
import Switch from 'infrastructure/js/components/controls/Switch/switch';
import Normalize from 'infrastructure/js/components/controls/controlsNormalizations';
import PermissionManager from 'infrastructure/js/utils/permissionManager';
import { AddRemoveListExt } from '../../../../../../Common/Controls/AddRemoveListExt/addRemoveListExt';
import ToolTypeKitTypeListItem from '../../../Common/ToolTypeKitTypeListItem/toolTypeKitTypeListItem';
import Parse from 'infrastructure/js/components/controls/controlsParse';
import Format from 'infrastructure/js/components/controls/controlsFormat';
import { enumTypes, getEnumValue, getLookupOptions } from '../../../../../../../utils/enumHelper';
import Tabs from 'infrastructure/js/components/Tabs/tabs';
import Checkbox from 'infrastructure/js/components/controls/Checkbox/checkbox.js';
import PL_MultiSelectField from 'infrastructure/js/components/controls/MultiSelectField/multiSelectField';
import withPortal from 'infrastructure/js/components/HOCs/withPortal/withPortal.js';

import * as V from './createToolTypeDialogValidation';
import { default as Logic } from './createToolTypeDialogLogic';


import './createToolTypeDialog.scss';


const ComboboxPortal = withPortal(Combobox);


class CreateToolTypeDialog extends React.PureComponent {

  constructor(props) {
    super(props);

    this.dialogLabels = createLabelHelper('mat.dialog.');
    this.labels = createLabelHelper('mat.administration.matsettings.dialog.createtooltype.');
    this.labelsScheduling = createLabelHelper('mat.administration.matsettings.kittypes.kitTypeForm.');
    this.dicLabels = createLabelHelper('dic.');


    this.itemToEdit = this.props.sData.get('itemToEdit');
    this.isEditMode = !!this.itemToEdit;

    this.kitTypesIdsInUse = this.isEditMode
      ? this.itemToEdit.kitTypesWithCapacity?.map((item) => item.kitTypeId)
      : [];

    this.preSelectedItems = this.getPreselectedItems();

    this.kitTypeConfigurationOptions = getLookupOptions(enumTypes.TOOL_CONFIGURATION_TYPE);
    let toolMaintenanceTypeOptions = getLookupOptions(enumTypes.TOOL_MAINTENANCE_TYPE);
    this.maintenanceTypeOptions = [
      { value: 'none', label: this.labels.get('maintenanceInterval.none') },
      ...toolMaintenanceTypeOptions
    ];


    this.state = {
      totalTokenValue: 0,
      kitTypeConfiguration: this.itemToEdit?.configurationType ?? getEnumValue(enumTypes.TOOL_CONFIGURATION_TYPE)('INTERCHANGEABLE'),
      continuousProduction: this.itemToEdit?.isContinuousProduction,
      activeTab: 'main',
      splitEnabled: false,
      maintenanceType: 'none',
      selectedOperation: null
    };
  }

  componentDidMount() {

    let initialValues;

    if (this.isEditMode) {
      // Use Logic class to get edit mode initial values
      initialValues = Logic.getEditModeInitialValues(this.itemToEdit);

      // Set initial state for edit mode
      const editModeState = Logic.getEditModeInitialState(this.itemToEdit, this.state);

      // Find the full operation object to get the manHours property
      if (this.itemToEdit.maintenanceOperation?.id) {
        const fullOperation = this.getAllOperations().find(op => op.value === this.itemToEdit.maintenanceOperation.id);
        if (fullOperation) {
          editModeState.selectedOperation = fullOperation;
          // Set the hidden field for validation
          initialValues._manHoursEnabled = fullOperation.data?.manHours === true;
        }
      }

      this.setState(editModeState);
    } else {
      // Use Logic class to get create mode initial values
      initialValues = Logic.getCreateModeInitialValues();
    }

    initialValues.toolCategory = this.isEditMode
      ? {
        value: this.itemToEdit.toolCategory?.id,
        label: this.itemToEdit.toolCategory?.businessId,
        data: this.itemToEdit.toolCategory
      }
      : this.getDefaultToolCategory();

    initialValues.kitTypes = this.isEditMode && this.itemToEdit.kitTypesWithCapacity?.length > 0 ?
      this.itemToEdit.kitTypesWithCapacity?.map((item) => {

        let tokensPerPart = this.getTokensByKitType(item.kitTypeId);
        return {
          kitTypeName: item.kitTypeId,
          capacity: item.capacity,
          tokenValue: (tokensPerPart && item.capacity) ? (tokensPerPart * item.capacity) : null,
        }
      }) :
      [{ capacity: 1 }];

    this.props.initialize(initialValues);

    setTimeout(() => { this.updateTotalTokenValue() }, 0);
  }


  onSubmit = (data) => {

    const newData = Logic.prepareSubmitData(
      data,
      this.createKitTypesData,
      this.isEditMode,
      this.itemToEdit
    );

    this.props.actions.submit(newData, this.isEditMode, this.props.reloadParentComponent);
  };








  getTokensByKitType = (kitTypeId) => {
    return this.getAllKitTypes()?.find((kt => kt.value === kitTypeId))?.data?.tokensPerPart || null;
  }

  getDefaultToolCategory = () => {
    let items = this.getCategories();

    let defaultCategory = items.find(item => item.data?.defaultCategory);
    return defaultCategory || null;
  }

  getDialogButtons() {
    return {
      left: [
        {
          id: 'cancel',
          text: this.dialogLabels.get('cancel'),
          action: this.props.actions.hide
        }
      ],
      right: [
        {
          id: 'submit',
          text: this.isEditMode ? this.dialogLabels.get('edit') : this.dialogLabels.get('create'),
          bsStyle: 'primary',
          loading: this.props.sData.get('loading'),
          action: this.props.handleSubmit(this.onSubmit),
          disabled: (this.isEditMode && this.props.pristine || this.props.sData.get('loading'))
        }
      ]
    };
  }

  getTools() {
    let dialogData = this.props.sData.get('dialogData');
    return dialogData?.toolTypes || [];
  }

  getCategories() {
    let dialogData = this.props.sData.get('dialogData');
    return dialogData?.toolCategories || [];
  }

  getStations() {
    let dialogData = this.props.sData.get('dialogData');
    return dialogData?.stations || [];
  }


  getAllOperations() {
    let dialogData = this.props.sData.get('dialogData');
    
    // console.log('dialogData?.operations ===> ', dialogData?.operations);
    
    return dialogData?.operations || [];
  }

  getDisallowedStationsOptions() {
    // If an operation is selected, return its specific stations
    if (this.state.selectedOperation) {
      const selectedOp = this.getAllOperations().find(op => op.value === this.state.selectedOperation.value);
      return selectedOp?.data?.stations || [];
    }

    // // If no operation selected, return all stations
    // return this.getStations();

    // If no operation selected, return empty list
    return [];
  }



  createKitTypesData = (kitTypes) => {
    return kitTypes.map((item) => {
      return { kitTypeId: item.kitTypeName, capacity: item.capacity }
    })
  }

  onHide = () => {
    this.props.actions.hide();
  };

  /**
   * Check if Main tab has all required fields filled and valid
   * Required fields: code, toolCategory, kitTypeConfiguration, kitTypes (at least one)
   */
  isMainTabValid = () => {
    
    const { syncErrors, formValues } = this.props;

    // Check for validation errors on required Main tab fields
    const mainTabFields = ['code', 'toolCategory', 'kitTypeConfiguration', 'kitTypes'];
    const hasFieldErrors = mainTabFields.some(field => syncErrors && syncErrors[field]);
    
    // console.log('formValues ===> ', formValues);

    if (hasFieldErrors) {
      return false;
    }

    // Check if required fields have values
    const hasCode = formValues?.code;
    const hasToolCategory = formValues?.toolCategory;
    const hasKitTypeConfiguration = formValues?.kitTypeConfiguration;

    // Check if at least one kit type has been selected (has kitTypeName)
    const hasKitTypes = formValues?.kitTypes?.some(item => item.kitTypeName);

    return hasCode && hasToolCategory && hasKitTypeConfiguration && hasKitTypes;
  };

  onTabSelect = (tabKey) => {
    // Prevent switching to Maintenance tab if Main tab is invalid
    if (tabKey === 'maintenance' && !this.isMainTabValid()) {
      return;
    }

    this.setState({ activeTab: tabKey });
  };

  onSplitEnabledClick = (event) => {
    this.setState({ splitEnabled: event.target.checked });
    if (!event.target.checked) {
      this.props.change('minimumSplitBlock', '');
      this.props.change('maximumTotalDuration', '');
    }
  };

  onMaintenanceTypeChange = (value) => {
    this.setState({ maintenanceType: value?.value || 'none' });
  };

  onOperationChangeCallback = (value) => {

    this.setState({ selectedOperation: value });

    // Clear disallowed stations when operation changes
    this.props.change('disallowedStations', []);

    // Store manHours value in form for validation access
    const manHoursEnabled = value?.data?.manHours === true;
    this.props.change('_manHoursEnabled', manHoursEnabled);

    // Clear max human capacity field if new operation doesn't support manHours
    if (!manHoursEnabled) {
      this.props.change('maxHumanCapacity', '');
    }

  };

  /**
   * Check if the selected operation requires manual hours (manHours)
   * @returns {boolean} - True if manHours is enabled for the selected operation
   */
  isManHoursEnabled = () => {
    return this.state.selectedOperation?.data?.manHours === true;
  };

  onKitTypeConfigurationChangeHandler = (newValue) => {
    this.setState({ kitTypeConfiguration: newValue?.value },
      () => {
        this.updateTotalTokenValue()
      });
  }

  getAllKitTypes = () => {
    let dialogData = this.props.sData.get('dialogData');
    return dialogData?.kitTypes || [];
  };

  onKitTypeListItemChanged = () => {
    setTimeout(() => { this.updateTotalTokenValue() }, 0);
  };

  onItemRemoveCallback = () => {
    setTimeout(() => { this.updateTotalTokenValue() }, 0);
  };

  updateTotalTokenValue = () => {
    this.setState({ totalTokenValue: this.getTotalTokenValue() });
  };

  getTotalTokenValue = () => {
    let allRows = this.props.fieldsValues;

    if (allRows) {
      if (this.state.kitTypeConfiguration === getEnumValue(enumTypes.TOOL_CONFIGURATION_TYPE)('PARALLEL')) {
        return allRows?.reduce((previous, current) => {
          //+(value) to convert string to number
          let value = current.tokenValue ?? 0;
          return previous + (+(value));
        }, 0);
      }
      else {
        let min = Math.min(...allRows.map(item => item.tokenValue ?? 0));
        let max = Math.max(...allRows.map(item => item.tokenValue ?? 0));
        return min === max ? min : `${min} - ${max}`;
      }
    }
    return null;
  }

  getPreselectedItems = () => {
    if (this.itemToEdit) {
      return this.itemToEdit.kitTypesWithCapacity?.map((item) => {
        return { ...item, tokensPerPart: this.getTokensByKitType(item.kitTypeId) }
      }) || [];
    }
    return [];
  }

  getKitTypeListItem = (itemProps) => {
    return (
      <ToolTypeKitTypeListItem
        key={itemProps.id + '-' + itemProps.index}
        {...itemProps}
        change={this.props.change}
        labels={this.labels}
        onItemChangeCallback={this.onKitTypeListItemChanged}
      />
    );
  }

  renderToolCategory = () => {
    return (
      <InputSection label={this.labels.get('toolCategory') + '*'} htmlFor="toolCategory"
        className="inline left-side">
        <Combobox id="toolCategory" name="toolCategory"
          isClearable={false}
          options={this.getCategories()}
          validate={Validation.required}
        />
      </InputSection>
    );
  }

  renderTotalTokens = () => {
    return (
      <div className="total-token-value-container">
        <label>{this.labels.get('list.totalTokenValue', undefined, { totalTokenValue: this.state.totalTokenValue })}</label>
      </div>
    )
  }

  renderKitTypesList = () => {
    let allOptions = this.getAllKitTypes();
    return (
      <div className="kit-types-list-container input-section">
        <div className="kit-types-list-title input-section">
          <label className="column">{this.labels.get('list.kitType') + '*'}</label>
          <label className="column">{this.labels.get('list.capacity') + '*'}</label>
          <label className="column">{this.labels.get('list.tokenValue')}</label>
        </div>
        <AddRemoveListExt className="kit-types-list"
          name='kitTypes'
          id='kitTypes'
          itemRendererComponent={this.getKitTypeListItem}
          itemRendererOptions={allOptions}
          preSelectedItemsIds={this.kitTypesIdsInUse}
          preSelectedItems={this.preSelectedItems}
          addButtonLabel={this.labels.get('list.addButton')}
          onRemoveCallback={this.onItemRemoveCallback}
          hideAddAtButton={true}
          defaultItem={{ capacity: 1 }}
          maxItemsToOverflow={4}
          minFieldsCount={1} />
        {this.renderTotalTokens()}
      </div>
    )
  }


  renderMaintenanceSchedulingSection = () => {

    if (this.state.maintenanceType === 'none') {
      return null;
    }

    if (!PermissionManager.getOrgPreferences().schedulerEnabled) {
      return null;
    }

    return (<>

      <div id="maintenance-scheduling-section">

        <div className="input-row">
          <label id="scheduling-heading">{this.labels.get('scheduling')}</label>
        </div>

        <div className="input-row">

          <InputSection label={this.labelsScheduling.get('operations.operation') + '*'} htmlFor="operationName" className="inline left-side no-margin">
            <ComboboxPortal
              id={'operationName'}
              name={'operationName'}
              options={this.getAllOperations()}
              parse={Parse.comboValueOnly()}
              format={Format.findOptionByValue(this.getAllOperations())}
              onChangeCallback={this.onOperationChangeCallback}
              className=""
              validate={V.validateSchedulingRequired}
            />
          </InputSection>

        </div>


        <div className="input-row">

          <InputSection label={this.labelsScheduling.get('operations.duration') + '*'} htmlFor="schedulingDuration" className="inline flow-left schedulingDuration-input">
            <TimeField id="schedulingDuration" name="schedulingDuration" hoursCount={true} maxHoursLength={3}
              validate={[
                V.validate_SchedulingDuration_Required,
                V.validate_SchedulingDuration_MinValue
              ]} />
          </InputSection>

          <InputSection label={this.labelsScheduling.get('operations.humanCapacityMin') + '*'} htmlFor="minHumanCapacity" className="inline flow-left minHumanCapacity-input">
            <TextField id="minHumanCapacity" name="minHumanCapacity"
              normalize={Normalize.number(true, 1, 999)}
              validate={V.validateSchedulingRequired} />
          </InputSection>

          <InputSection label={this.labelsScheduling.get('operations.humanCapacityMax') + '*'} htmlFor="maxHumanCapacity" className="inline flow-left maxHumanCapacity-input">
            <TextField id="maxHumanCapacity" name="maxHumanCapacity"
              normalize={Normalize.number(true, 1, 999)}
              disabled={!this.isManHoursEnabled()}
              validate={[
                V.validate_MaxHumanCapacity_Required,
                V.validate_MaxHumanCapacity_MinValue
              ]} />
          </InputSection>

        </div>

        <div className="input-row">

          <InputSection htmlFor="splitEnabled" className="inline flow-left splitEnabled-input">
            <Checkbox id="splitEnabled" name="splitEnabled" label={this.labelsScheduling.get('operations.splitEnabled')}
              onClick={this.onSplitEnabledClick} />
          </InputSection>

          <InputSection label={this.labelsScheduling.get('operations.minimumSplitBlock') + '*'} htmlFor="minimumSplitBlock" className="inline flow-left">
            <TimeField id="minimumSplitBlock" name="minimumSplitBlock"
              hoursCount={true} maxHoursLength={3}
              disabled={!this.state.splitEnabled}
              validate={[
                V.validate_MinimumSplitBlock_Required,
                V.validate_MinimumSplitBlock_MinValue
              ]} />
          </InputSection>

          <InputSection label={this.labelsScheduling.get('operations.maximumTotalDuration') + '*'} htmlFor="maximumTotalDuration" className="inline flow-left">
            <TimeField id="maximumTotalDuration" name="maximumTotalDuration"
              hoursCount={true} maxHoursLength={3}
              disabled={!this.state.splitEnabled}
              validate={[
                V.validate_MaximumTotalDuration_Required,
                V.validate_MaximumTotalDuration_MinValue
              ]}
            />
          </InputSection>

        </div>

        <div className="input-row">

          <InputSection label={this.labelsScheduling.get('operations.disallowedStations')} htmlFor="disallowedStations" className="full-width">
            <PL_MultiSelectField
              id="disallowedStations"
              name="disallowedStations"
              parse={Parse.comboValuesOnly()}
              format={Format.findOptionsByValues(this.getDisallowedStationsOptions())}
              options={this.getDisallowedStationsOptions()}
              closeMenuOnSelect={false}
            />
          </InputSection>

        </div>

      </div>


    </>);
  }






  render() {
    let titleText = this.isEditMode ? this.labels.get('header.title.edit') : this.labels.get('header.title.create');

    return (
      <Dialog
        id="create-tool-type-dialog"
        className="create-tool-type-dialog"
        titleText={titleText}
        show={this.props.sData.get('show')}
        onEntered={this.onEntered}
        onHide={this.onHide}
        sData={this.props.sData}
        footerButtons={this.getDialogButtons()}
        onEnterKeyPress={this.props.handleSubmit(this.onSubmit)}
      >
        <Tabs id="create-tool-type-dialog-tabs" activeKey={this.state.activeTab} tabType="dialog" onSelect={this.onTabSelect}>
          <Tabs.Tab eventKey="main" title={this.labels.get('tabMain')} animation={false}>
            <div className="tab-content-wrapper">
              <InputSection label={this.labels.get('code') + '*'} htmlFor="code" className="inline left-side no-margin">
                <TextField id="code" name="code" className="short-textfield"
                  validate={[Validation.required, V.maxLength100]}
                />
              </InputSection>

              <InputSection label={this.labels.get('name')} htmlFor="name" className="inline right-side no-margin">
                <Combobox id="name" name="name" options={this.getTools()}
                  allowNewOption={true}
                  validate={[V.maxLengthDropDown]}
                />
              </InputSection>

              {this.renderToolCategory()}

              {/* <InputSection label={this.labels.get('maintenanceUsageTime')} htmlFor="maintenanceUsageTime" className="inline right-side">
                <TimeField id="maintenanceUsageTime" name="maintenanceUsageTime" className="inline right-side" isDays={true} hoursCount={true} />
              </InputSection>

              <InputSection label={this.labels.get('cyclecount')} htmlFor="cycleCount" className="inline left-side">
                <TextField id="cycleCount" name="cycleCount" className="inline right-side" normalize={Normalize.number(true, 0, 9999)} />
              </InputSection>

              {
                PermissionManager.getOrgPreferences().schedulerEnabled ?
                  <InputSection label={this.labels.get('maintenanceDuration')} htmlFor="maintenanceDuration" className="inline right-side">
                    <TimeField id="maintenanceDuration" name="maintenanceDuration" className="inline left-side" hoursCount={true} maxHoursLength={3} />
                  </InputSection>
                  : null
              } */}
              <div className="clear-both" />

              <InputSection label={this.labels.get('kitTypeConfiguration') + '*'} htmlFor="kitTypeConfiguration"
                className='inline left-side'>
                {/* className={PermissionManager.getOrgPreferences().schedulerEnabled ? 'inline left-side' : 'inline right-side'}> */}
                <Combobox id="kitTypeConfiguration" name="kitTypeConfiguration"
                  isClearable={false}
                  options={this.kitTypeConfigurationOptions}
                  validate={Validation.required}
                  parse={Parse.comboValueOnly()}
                  format={Format.findOptionByValue(this.kitTypeConfigurationOptions)}
                  onChangeCallback={this.onKitTypeConfigurationChangeHandler}
                />
              </InputSection>

              <InputSection label={this.labels.get('continuousProduction')} htmlFor="continuousProduction"
                className={PermissionManager.getOrgPreferences().schedulerEnabled ? 'inline right-side' : 'inline left-side'}>
                <div className='switch-section'>
                  <Switch id="continuousProduction" name="continuousProduction" />
                </div>
              </InputSection>

              {this.renderKitTypesList()}
            </div>
          </Tabs.Tab>


          <Tabs.Tab eventKey="maintenance" title={this.labels.get('tabMaintenance')} animation={false} disabled={!this.isMainTabValid()}>

            <div className="tab-content-wrapper">

              <div id="maintenance-type-section">

                <div className="input-row">

                  <InputSection label={this.labels.get('maintenanceType')} htmlFor="maintenanceType" className="inline left-side no-margin">
                    <Combobox
                      id="maintenanceType"
                      name="maintenanceType"
                      isClearable={false}
                      options={this.maintenanceTypeOptions}
                      parse={Parse.comboValueOnly()}
                      format={Format.findOptionByValue(this.maintenanceTypeOptions)}
                      onChangeCallback={this.onMaintenanceTypeChange}
                    />
                  </InputSection>

                  {this.state.maintenanceType === 'TIME_BASED' && (
                    <InputSection label={this.labels.get('interval') + "*"} htmlFor="timeBasedInterval" className="inline right-side no-margin">
                      <TimeField id="timeBasedInterval" name="timeBasedInterval" className="inline" isDays={true} hoursCount={true} isMinutes={false}
                        validate={[
                          V.validate_MaintenanceTimeBasedInterval_Required,
                          V.validate_MaintenanceTimeBasedInterval_MinValue
                        ]}
                      />
                    </InputSection>
                  )}

                  {this.state.maintenanceType === 'USAGE_BASED' && (
                    <InputSection label={this.labels.get('usageTime') + "*"} htmlFor="usageBasedInterval" className="inline right-side no-margin">
                      <TimeField id="usageBasedInterval" name="usageBasedInterval" className="inline" isDays={true} hoursCount={true} isMinutes={false}
                        validate={[
                          V.validate_MaintenanceUsageBasedInterval_Required,
                          V.validate_MaintenanceUsageBasedInterval_MinValue
                        ]}
                      />
                    </InputSection>
                  )}

                  {this.state.maintenanceType === 'CYCLE_BASED' && (
                    <InputSection label={this.dicLabels.get('cycle.pascals') + "*"} htmlFor="cycleBasedInterval" className="inline right-side no-margin">
                      <TextField id="cycleBasedInterval" name="cycleBasedInterval" className="inline" normalize={Normalize.number(true, 1, 9999999)}
                        validate={V.validate_MaintenanceCycleBasedInterval_Required} />
                    </InputSection>
                  )}

                </div>

              </div>

              {this.renderMaintenanceSchedulingSection()}


            </div>


          </Tabs.Tab>




        </Tabs>
      </Dialog>
    );
  }
}

CreateToolTypeDialog = reduxForm({
  form: 'createToolTypeDialog',
}
)(CreateToolTypeDialog);


const selector = formValueSelector('createToolTypeDialog');
const formName = 'createToolTypeDialog';

export default CreateToolTypeDialog = connect(state => {
  return {
    fieldsValues: selector(state, 'kitTypes'),
    formValues: getFormValues(formName)(state),
    syncErrors: getFormSyncErrors(formName)(state)
  }
})(CreateToolTypeDialog);

CreateToolTypeDialog.propTypes = {
  actions: PropTypes.object.isRequired,
  sData: PropTypes.object.isRequired,
};



